import { createAsyncThunk } from '@reduxjs/toolkit';

import { ICustomSubmission } from '.';
import APIDataProvider, { TApiResponse } from '../../../api/ApiDataProvider';
import { ICustomQuiz } from '../CustomQuizSlice';
import { ApiPaths } from '../../../api/APIPaths';
import { IQuestion } from '../../QuestionSlice';

export interface ICreateNewSubmissionByCustomQuiz {
  section: string;
  customQuiz: string;
}

export interface IUpdateQuestionTimer {
  submissionId: string;
  questionId: string;
}

export interface ISubmissionCreateAnswer {
  submissionId: string;
  questionId: string;
  answerIds?: string[];
  answerId?: string;
}

export interface ISubmissionRemoveAnswer {
  submissionId: string;
  questionId: string;
}

export interface ISubmissionFinish {
  submissionId: string;
}

export interface ISubmissionGetQuestion {
  submissionId: string;
  questionId: string;
}

export interface IUpdateSubmissionQuestionOpenAiFeedback {
  submissionId: string;
  questionId: string;
  feedback: -1 | 0 | 1;
}

const apiDataProvider = new APIDataProvider();

export const createCustomSubmission = createAsyncThunk(
  '/custom-submission/create',
  async (data: ICreateNewSubmissionByCustomQuiz) => {
    const response = await apiDataProvider.post<
      TApiResponse<{
        submission: ICustomSubmission;
        customQuiz: ICustomQuiz;
      }>
    >(`${ApiPaths.customSubmission}/create`, data);

    return response.data;
  }
);

export const getCustomSubmissionById = createAsyncThunk(
  '/custom-submission/id',
  async (id: string) => {
    const response = await apiDataProvider.get<TApiResponse<ICustomSubmission>>(
      `${ApiPaths.customSubmission}/${id}`
    );

    return response.data;
  }
);

export const startCustomSubmissionQuestionTimer = createAsyncThunk(
  '/custom-submission/startSubmissionQuestionTimer',
  async (data: IUpdateQuestionTimer) => {
    const response = await apiDataProvider.post<
      TApiResponse<{
        submissionId?: string;
        questionId?: string;
        timerStart?: number;
      }>
    >(`${ApiPaths.customSubmission}/startSubmissionQuestionTimer`, data);

    return response.data;
  }
);

export const endCustomSubmissionQuestionTimer = createAsyncThunk(
  '/custom-submission/endSubmissionQuestionTimer',
  async (data: IUpdateQuestionTimer) => {
    const response = await apiDataProvider.post<
      TApiResponse<{
        submissionId?: string;
        questionId?: string;
        timerEnd?: number;
      }>
    >(`${ApiPaths.customSubmission}/endSubmissionQuestionTimer`, data);

    return response.data;
  }
);

export const createCustomSubmissionAnswer = createAsyncThunk(
  '/custom-submission/create/answer',
  async (data: ISubmissionCreateAnswer) => {
    const response = await apiDataProvider.post<
      TApiResponse<ICustomSubmission>
    >(`${ApiPaths.customSubmission}/answer/add`, data);

    return response.data;
  }
);

export const removeCustomSubmissionAnswer = createAsyncThunk(
  '/custom-submission/remove/answer',
  async (data: ISubmissionRemoveAnswer) => {
    const response = await apiDataProvider.post<
      TApiResponse<ICustomSubmission>
    >(`${ApiPaths.customSubmission}/answer/remove`, data);

    return response.data;
  }
);

export const finishCustomSubmission = createAsyncThunk(
  '/custom-submission/finish',
  async (data: ISubmissionFinish) => {
    const response = await apiDataProvider.post<
      TApiResponse<ICustomSubmission>
    >(`${ApiPaths.customSubmission}/finish`, data);

    return response.data;
  }
);

export const getCustomSubmissionQuestion = createAsyncThunk(
  '/custom-submission/question/get',
  async (data: ISubmissionGetQuestion) => {
    const response = await apiDataProvider.post<TApiResponse<IQuestion>>(
      `${ApiPaths.customSubmission}/question/get`,
      data
    );
    return response.data;
  }
);

export const getCustomSubmissionAiFeedback = createAsyncThunk(
  '/custom-submission/get-ai-feedback',
  async (data: { submissionId: string }) => {
    const response = await apiDataProvider.get<TApiResponse<any>>(
      `${ApiPaths.customSubmission}/get-ai-feedback/${data.submissionId}`
    );

    return response.data;
  }
);

export const getSubmissionQuestionAIExplanation = createAsyncThunk(
  '/custom-submission/question/get-ai-explanation',
  async (data: ISubmissionGetQuestion) => {
    const response = await apiDataProvider.post<TApiResponse<string>>(
      `${ApiPaths.customSubmission}/question/get-ai-explanation`,
      data
    );

    return response.data;
  }
);

export const updateCustomSubmissionQuestionOpenAiFeedback = createAsyncThunk(
  'custom-submission/updateQuestionOpenAiFeedback',
  async (data: IUpdateSubmissionQuestionOpenAiFeedback) => {
    const response = await apiDataProvider.post<
      TApiResponse<IUpdateSubmissionQuestionOpenAiFeedback>
    >(`${ApiPaths.customSubmission}/updateQuestionOpenAiFeedback`, data);

    return response.data;
  }
);
